import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  Chip,
} from '@mui/material';
import { HourglassEmpty, CheckCircle, Refresh } from '@mui/icons-material';
import {
  childManagementService,
  type PendingLinkRequest,
} from '../services/childManagementService';

interface ChildPendingLinksFormProps {
  onLinkCompleted: () => void;
}

const ChildPendingLinksForm: React.FC<ChildPendingLinksFormProps> = ({ onLinkCompleted }) => {
  const [pending, setPending] = useState<PendingLinkRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [codes, setCodes] = useState<Record<number, string>>({});
  const [submittingId, setSubmittingId] = useState<number | null>(null);

  const loadPending = async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await childManagementService.getPendingLinkRequests();
      setPending(items);
    } catch (err: any) {
      setError(err.message || 'Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const handleVerify = async (item: PendingLinkRequest) => {
    const code = (codes[item.relationshipId] || '').trim();
    if (!/^\d{6}$/.test(code)) {
      setError('Please enter a valid 6-digit code.');
      return;
    }

    setSubmittingId(item.relationshipId);
    setError(null);
    setSuccess(null);

    try {
      await childManagementService.verifyLinkCode(item.relationshipId, code);
      setSuccess('Child linked successfully!');
      setCodes((prev) => {
        const next = { ...prev };
        delete next[item.relationshipId];
        return next;
      });
      await loadPending();
      onLinkCompleted();
    } catch (err: any) {
      setError(err.message || 'Failed to verify code');
    } finally {
      setSubmittingId(null);
    }
  };

  const handleResend = async (relationshipId: number) => {
    setSubmittingId(relationshipId);
    setError(null);
    setSuccess(null);

    try {
      await childManagementService.resendLinkCode(relationshipId);
      setSuccess('A new verification code has been sent to the student.');
      await loadPending();
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setSubmittingId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
        Pending Link Requests
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Complete a link by entering the verification code your child received by email.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {pending.length === 0 ? (
        <Alert severity="info" icon={<HourglassEmpty />}>
          No pending link requests. Use the &quot;Add Registered Child&quot; tab to search for a
          student and send a verification code.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {pending.map((item) => (
            <Card key={item.relationshipId} variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
                  <Typography variant="h5" sx={{ letterSpacing: 2, fontWeight: 600 }}>
                    {item.initials}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.childEmail}
                  </Typography>
                  <Chip
                    size="small"
                    label={item.status}
                    color={item.locked ? 'warning' : item.codeExpired ? 'default' : 'info'}
                  />
                </Stack>

                {item.codeExpired && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    The previous code may have expired. Resend a new code to the student.
                  </Typography>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <TextField
                    label="6-digit code"
                    value={codes[item.relationshipId] || ''}
                    onChange={(e) =>
                      setCodes((prev) => ({
                        ...prev,
                        [item.relationshipId]: e.target.value.replace(/\D/g, '').slice(0, 6),
                      }))
                    }
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    size="small"
                    sx={{ minWidth: 160 }}
                    disabled={submittingId === item.relationshipId}
                  />
                  <Button
                    variant="contained"
                    startIcon={
                      submittingId === item.relationshipId ? (
                        <CircularProgress size={18} />
                      ) : (
                        <CheckCircle />
                      )
                    }
                    disabled={
                      submittingId === item.relationshipId ||
                      (codes[item.relationshipId] || '').length !== 6
                    }
                    onClick={() => handleVerify(item)}
                  >
                    Complete link
                  </Button>
                  <Button
                    startIcon={<Refresh />}
                    disabled={submittingId === item.relationshipId}
                    onClick={() => handleResend(item.relationshipId)}
                  >
                    Resend code
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ChildPendingLinksForm;
